import {
  Button,
  Alert,
  FormGroup,
  Form,
  Container,
  Row,
  Col,
} from "reactstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import Cookies from "js-cookie";

// core components
import AppHelmet from "../../components/AppComponents/AppHelmet";
import IndexNavbar from "../../components/Navbars/IndexNavbar";
import AuthFooter from "../../components/Footers/AuthFooter";
import PendingApprovalModal from "./PendingApprovalModal";
import AppSpinner from "../../elements/AppSpinner";
import RightArrow from "../../elements/RightArrow";
import AppInput from "../../components/AppInput";
import API from "../../api/api/API";
import { useAppConfig } from "./Register";
import { AppConfirm } from "../../utils/PopupUtil";
import { getErrorMessage } from "../../utils/GeneralUtils";

const EnterPhoneVerification = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [registrationToken, setRegistrationToken] = useState("");
  const [identityToken, setIdentityToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittingResend, setSubmittingResend] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const queryClient = useQueryClient();
  const [isPendingModalOpen, setPendingModalOpen] = useState(false);
  const [search, setSearch] = useSearchParams();
  const appConfig = useAppConfig(search.get("app"));

  const onCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
  };

  useEffect(() => {
    const phone = Cookies.get("phone") || search.get("phone");
    const registrationToken =
      Cookies.get("registrationToken") || search.get("registrationToken");
    const identityToken =
      Cookies.get("identityToken") || search.get("identityToken");

    if (!phone || !registrationToken || !identityToken) {
      navigate("/register/phone");
      return;
    }

    setPhone(phone);
    setRegistrationToken(registrationToken);
    setIdentityToken(identityToken);
  }, []);

  const formatPhoneEnd = (phone: string) => {
    return phone ? phone.slice(-4) : "";
  };

  const handleAppRedirect = async (
    authenticationToken: string,
    refreshToken: string,
    customClientID: string
  ) => {
    const redirectTo = search.get("redirectTo");

    if (!redirectTo) {
      AppConfirm({
        title: "Unexpected Error",
        text: "Please restart the process and try again.",
        confirmButtonText: "OK",
      });
      return;
    }

    window.location.href = `${redirectTo}?authenticationToken=${authenticationToken}&refreshToken=${refreshToken}&clientID=${customClientID}`;
  };

  const verifyIdentity = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSubmitting(true);

    API.verifyIdentityAndRegister(
      registrationToken,
      identityToken,
      code,
      !!appConfig,
      search.get("app") || undefined
    )
      .then(
        (data) => {
          const {
            authenticationToken,
            refreshToken,
            user,
            existing,
            customClientID,
          } = data.data.data;

          queryClient.setQueryData(["user", null], (prev) => ({
            ...(prev || {}),
            user,
          }));

          if (appConfig) {
            return handleAppRedirect(
              authenticationToken,
              refreshToken,
              customClientID
            );
          }

          Cookies.set("authenticationToken", authenticationToken, {
            expires: 7,
            sameSite: "strict",
            secure: window.location.origin.startsWith("https"),
          });

          Cookies.remove("registrationToken");
          Cookies.remove("identityToken");
          Cookies.remove("phone");

          // Handle existing user, meaning they already have an account with the provided phone number
          if (existing) {
            return navigate("/register/existing");
          }

          if (user?.Status === "Pending") {
            setPendingModalOpen(true);
          } else {
            navigate("/clouds");
          }
        },
        (error) => {
          if (error && error.response && error.response.status === 401) {
            setError(
              "The verification code you provided is invalid or has expired. Click 'Resend Code' to receive a new one."
            );
          } else {
            setError(
              "We're having trouble verifying your phone number. Please try again."
            );
          }

          AppConfirm({
            title: "Error",
            text: getErrorMessage(error),
            confirmButtonText: "OK",
          });
          console.error(error);
        }
      )
      .finally(() => setSubmitting(false));
  };

  const resendVerification = () => {
    setSubmittingResend(true);

    API.sendIdentityVerification(phone)
      .then(
        (data) => {
          const identityToken = data.data.data.identityToken;

          Cookies.set("identityToken", identityToken, {
            sameSite: "strict",
            secure: window.location.origin.startsWith("https"),
          });

          if (appConfig) {
            search.set("identityToken", identityToken);
            setSearch(search);
          }

          setResendSuccess(true);
          setIdentityToken("");

          setTimeout(() => {
            setResendSuccess(false);
          }, 3000);
        },
        (error) => {
          setError(error?.response?.data?.message);
        }
      )
      .finally(() => setSubmittingResend(false));
  };

  const handleModalClose = () => {
    setPendingModalOpen(false);
    navigate("/login");
  };

  return (
    <>
      <AppHelmet title="Enter Your Account Verification Code - GrowthCloud.com" />
      <div className="main-content">
        <IndexNavbar bg="" logo={appConfig?.Logo} />
        <Container
          fluid
          className="pb-5"
          style={{ height: "calc(100vh - 125px - 3rem)" }}
        >
          <div
            style={{
              width: "100%",
              minHeight: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "100%",
                flexGrow: 1,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Row className="justify-content-center mt-4 mt-sm-6">
                <Col lg="6" xl="5" md="8">
                  <div>
                    <h2 className="text-dark mb-4 mt-5">Verify Your Account</h2>
                    <div
                      className="mt-0 font-weight-500 text-dark mb-3"
                      style={{ fontSize: 15 }}
                    >
                      We've sent a verification code to your phone number ending
                      in {formatPhoneEnd(phone)}. Enter the verification code
                      you received to continue.
                    </div>
                    <div
                      className="mt-0 font-weight-500 text-dark mb-4"
                      style={{ fontSize: 15 }}
                    >
                      Didn't receive it?
                      <a
                        href="#"
                        className={`tw:text-primary ${
                          submittingResend || resendSuccess ? "disabled" : ""
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (submittingResend || resendSuccess) return;
                          resendVerification();
                        }}
                      >
                        {" "}
                        {submittingResend ? (
                          <>Sending...</>
                        ) : (
                          <>
                            {resendSuccess
                              ? "Successfully resent"
                              : "Resend code"}
                          </>
                        )}{" "}
                        <RightArrow />
                      </a>
                    </div>

                    <Form role="form" onSubmit={verifyIdentity}>
                      <FormGroup>
                        <AppInput
                          className="tw:text-left tw:text-dark tw:focus:border-primary"
                          onChange={onCodeChange}
                          placeholder={"123456"}
                          autoFocus
                        />
                      </FormGroup>
                      <div className="tw:text-left">
                        <Button
                          className="tw:mb-4 tw:px-4 tw:bg-primary tw:border-primary"
                          color="primary"
                          type="submit"
                          disabled={!code || submitting}
                        >
                          {!submitting ? (
                            <>Verify Account</>
                          ) : (
                            <>
                              <AppSpinner size="sm" color="white" />
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                    {error && <Alert color="danger">{error}</Alert>}
                    <div className="mt-0">
                      <p
                        className="text-light mb-0"
                        style={{ lineHeight: 1.2 }}
                      >
                        <small>
                          By clicking Resend Code, you agree to our Privacy
                          Policy and Terms of Use. Standard text and data rates
                          may apply.
                        </small>
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          <div className="mt-5">
            <AuthFooter />
          </div>
        </Container>
      </div>
      <PendingApprovalModal
        open={isPendingModalOpen}
        onClose={handleModalClose}
      />
    </>
  );
};

export default EnterPhoneVerification;
